import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { SiteData } from '@/types/api';

/**
 * Admin websites API. A Site is a client's website — it owns the blog content
 * key today and will own form endpoints next, so this is the surface for
 * creating one and rotating its secrets.
 *
 * Portal users reach sites through `useMySites` in ./blog instead; every hook
 * here hits an admin-gated route.
 */

export type SiteFilters = {
  status?: string;
  q?: string;
  page?: number;
};

/** Payload for create/update — the mutable columns, never the key hashes. */
export type SiteInput = {
  name?: string;
  domain?: string | null;
  staging_url?: string | null;
  status?: string;
  brand_voice?: string | null;
  production_build_hook_url?: string | null;
  staging_build_hook_url?: string | null;
};

const SITES_KEY = ['sites'] as const;
const SITE_KEY = (id: number) => ['sites', id] as const;

export function useSites(filters: SiteFilters = {}) {
  return useQuery({
    queryKey: [...SITES_KEY, filters],
    queryFn: async (): Promise<Paginated<SiteData>> => {
      const { data } = await api.get<Paginated<SiteData>>('/v1/sites', { params: filters });
      return data;
    },
  });
}

export function useSite(id: number | undefined) {
  return useQuery({
    queryKey: id ? SITE_KEY(id) : ['sites', 'none'],
    enabled: id != null,
    queryFn: async (): Promise<SiteData> => {
      const { data } = await api.get<SiteData>(`/v1/sites/${id}`);
      return data;
    },
  });
}

export function useClientSites(clientId: number | undefined) {
  return useQuery({
    queryKey: ['clients', clientId, 'sites'],
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<SiteData>> => {
      const { data } = await api.get<Paginated<SiteData>>(`/v1/clients/${clientId}/sites`);
      return data;
    },
  });
}

export function useCreateSite(clientId: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SiteInput): Promise<SiteData> => {
      await primeCsrf();
      const { data } = await api.post<SiteData>(`/v1/clients/${clientId}/sites`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SITES_KEY }),
  });
}

export function useUpdateSite(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SiteInput): Promise<SiteData> => {
      await primeCsrf();
      const { data } = await api.patch<SiteData>(`/v1/sites/${id}`, patch);
      return data;
    },
    onSuccess: (site) => {
      qc.setQueryData(SITE_KEY(id), site);
      void qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/sites/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SITES_KEY }),
  });
}

/**
 * Rotate a secret. The plaintext comes back exactly once — the caller must
 * show it immediately, because it is unrecoverable after this response.
 */
export function useRegenerateSiteKey(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ api_key: string; site: SiteData }> => {
      await primeCsrf();
      const { data } = await api.post<{ api_key: string; site: SiteData }>(
        `/v1/sites/${id}/regenerate-key`,
      );
      return data;
    },
    onSuccess: ({ site }) => {
      qc.setQueryData(SITE_KEY(id), site);
      void qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

export function useRegeneratePreviewToken(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ preview_token: string; site: SiteData }> => {
      await primeCsrf();
      const { data } = await api.post<{ preview_token: string; site: SiteData }>(
        `/v1/sites/${id}/regenerate-preview-token`,
      );
      return data;
    },
    onSuccess: ({ site }) => {
      qc.setQueryData(SITE_KEY(id), site);
      void qc.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}
